import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Paper, Portal, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { alpha, useTheme, type Theme } from '@mui/material/styles';
import { getCommonStyles, getOnboardingCardSurfaceStyles, onboardingActionButtonSx, onboardingStepTextSx } from './constants';

export type AnchoredTourStep = {
  title: string;
  description: string;
  targetTestIds: readonly string[];
  highlightTargetTestIds?: readonly string[];
  /** When true, expand spotlight to the enclosing dialog (use with highlightTargetTestIds). */
  highlightExpandModal?: boolean;
  /** Optional frame around a related modal (e.g. parent dialog behind a nested spotlight). */
  contextTargetTestIds?: readonly string[];
  /** Prefer placing the tour card above or below the highlight (default: below, then above). */
  panelPlacement?: 'auto' | 'above' | 'below' | 'left';
  /** Extra clickable area below the highlight (e.g. for MUI Select / Autocomplete menus). */
  interactionPaddingBelow?: number;
  /** Top padding for the highlight frame; use 0 when a fixed label sits directly above the target. */
  highlightPaddingTop?: number;
  /** On the final step, complete the tour when the user clicks these targets (e.g. form submit). */
  completeWhenTargetClicked?: readonly string[];
  /** Disable Back when the previous step target is no longer reachable (e.g. sidebar nav after leaving that page). */
  disableBack?: boolean;
  /** Show Next / Mark Complete only after one of these targets is in the DOM. */
  showPrimaryWhenTargetTestIds?: readonly string[];
  /** Click the enclosing dialog close control when the user presses Back on this step. */
  closeTargetTestIdsOnBack?: readonly string[];
  /** Dismiss overlays (dialogs, popovers) when the user presses Next on this step. */
  closeTargetTestIdsOnNext?: readonly string[];
};

export type AnchoredTourHighlightRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

export const TOUR_HIGHLIGHT_PADDING = 8;
export const TOUR_HIGHLIGHT_PADDING_COMPACT = 4;

export const getTourHighlightPadding = (target: HTMLElement | null): number => {
  if (!target) {
    return TOUR_HIGHLIGHT_PADDING;
  }

  const focus =
    target.closest('button, a[href], [role="button"], .MuiIconButton-root, .MuiButton-root, .MuiTab-root') ?? target;

  if (focus.matches('button, a[href], [role="button"], .MuiIconButton-root, .MuiButton-root, .MuiTab-root')) {
    return TOUR_HIGHLIGHT_PADDING_COMPACT;
  }

  if (focus.matches('.MuiListItem-root')) {
    return 6;
  }

  return TOUR_HIGHLIGHT_PADDING;
};

const FLOATING_LABEL_HIGHLIGHT_GAP = 4;

/** Push the highlight top edge above a shrunk MUI InputLabel so the frame does not cross label text. */
export const getFloatingLabelTopClearance = (
  target: HTMLElement | null | undefined,
  targetRect: DOMRect,
): number => {
  if (!target) {
    return 0;
  }

  const formControl =
    (target.classList.contains('MuiFormControl-root') ? target : null) ??
    target.closest('.MuiFormControl-root') ??
    target.querySelector('.MuiFormControl-root');

  if (!(formControl instanceof HTMLElement)) {
    return 0;
  }

  const label = formControl.querySelector('.MuiInputLabel-root');

  if (!(label instanceof HTMLElement)) {
    return 0;
  }

  const isShrunkLabel =
    label.classList.contains('MuiInputLabel-shrink') || label.classList.contains('Mui-focused');

  if (!isShrunkLabel) {
    return 0;
  }

  const labelRect = label.getBoundingClientRect();
  const padding = getTourHighlightPadding(target);
  const highlightTop = targetRect.top - padding;
  const desiredTop = labelRect.top - FLOATING_LABEL_HIGHLIGHT_GAP;

  return Math.max(0, highlightTop - desiredTop);
};

export type TourHighlightRectOptions = {
  paddingTop?: number;
};

export const toTourHighlightRect = (
  targetRect: DOMRect | null,
  target?: HTMLElement | null,
  options?: TourHighlightRectOptions,
): AnchoredTourHighlightRect | null => {
  if (!targetRect) {
    return null;
  }

  const padding = getTourHighlightPadding(target ?? null);
  const paddingTop = options?.paddingTop ?? padding;
  const topClearance = getFloatingLabelTopClearance(target ?? null, targetRect);

  return {
    top: Math.max(8, targetRect.top - paddingTop - topClearance),
    left: Math.max(8, targetRect.left - padding),
    width: targetRect.width + padding * 2,
    height: targetRect.height + paddingTop + padding + topClearance,
  };
};

export const unionTourHighlightRects = (
  ...rects: (AnchoredTourHighlightRect | null | undefined)[]
): AnchoredTourHighlightRect | null => {
  const valid = rects.filter((rect): rect is AnchoredTourHighlightRect => Boolean(rect));

  if (!valid.length) {
    return null;
  }

  if (valid.length === 1) {
    return valid[0];
  }

  const top = Math.min(...valid.map(rect => rect.top));
  const left = Math.min(...valid.map(rect => rect.left));
  const right = Math.max(...valid.map(rect => rect.left + rect.width));
  const bottom = Math.max(...valid.map(rect => rect.top + rect.height));

  return {
    top,
    left,
    width: right - left,
    height: bottom - top,
  };
};

export const TOUR_SCROLL_MARGIN_BOTTOM = '340px';
export const TOUR_SCROLL_MARGIN_TOP = '80px';
export const TOUR_UI_SETTLE_MS = 320;
export const TOUR_TARGET_POLL_MS = 300;
export const TOUR_TARGET_TIMEOUT_MS = 180_000;
export const TOUR_MOTION_MS = 200;
/** @deprecated Use TOUR_MOTION_MS */
export const TOUR_TRANSITION_MS = TOUR_MOTION_MS;
export const TOUR_HIGHLIGHT_SYNC_MS = 100;

const TOUR_POSITION_TRANSITION = `top ${TOUR_MOTION_MS}ms ease-out, left ${TOUR_MOTION_MS}ms ease-out`;
export const TOUR_PANEL_Z_INDEX = 1700;
export const TOUR_GUIDE_PANEL_SELECTOR = '[data-tour-guide-panel]';
/** Below tour dialogs so modals and their fields stay fully clickable. */
const TOUR_BLOCKER_Z_INDEX = TOUR_PANEL_Z_INDEX - 52;
/** Tour target dialogs: above page blockers, below the tour guide panel. */
export const TOUR_DIALOG_Z_INDEX = TOUR_PANEL_Z_INDEX - 50;
/** Stacked dialog opened on top of another tour dialog (e.g. customize after add recipients). */
export const TOUR_NESTED_DIALOG_Z_INDEX = TOUR_PANEL_Z_INDEX - 25;
/** Above tour dialogs so highlights on modal controls remain visible. */
const TOUR_FRAME_Z_INDEX = TOUR_PANEL_Z_INDEX - 20;
/** Menus/popovers from tour targets must stack above the tour panel (not just blockers). */
export const TOUR_MENU_Z_INDEX = TOUR_PANEL_Z_INDEX + 100;

export const findTourTarget = (testIds: readonly string[]) => {
  for (const testId of testIds) {
    const target = document.querySelector(`[data-testid="${testId}"]`) as HTMLElement | null;

    if (target) {
      return target;
    }
  }

  return null;
};

/** Dismiss the MUI dialog that contains a tour target (title-bar X or footer Close). */
export const dismissTourTargetModal = (testId: string) => {
  const target = findTourTarget([testId]);

  if (!target) {
    return false;
  }

  const popover = target.closest('.MuiPopover-root');

  if (popover instanceof HTMLElement) {
    document.dispatchEvent(
      new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', keyCode: 27, bubbles: true, cancelable: true }),
    );

    return true;
  }

  const toggleControl =
    target.matches('button, [role="button"]') ? target : target.closest('button, [role="button"]');

  if (toggleControl instanceof HTMLElement) {
    const isExpanded = toggleControl.getAttribute('aria-expanded') === 'true';
    const hasOpenPopover = Boolean(
      document.querySelector('.MuiPopover-root:not([aria-hidden="true"])'),
    );

    if (isExpanded || hasOpenPopover) {
      toggleControl.click();
      return true;
    }
  }

  const dialog = target.closest('[role="dialog"]') ?? target.closest('.MuiDialog-root');

  if (!(dialog instanceof HTMLElement)) {
    return false;
  }

  const titleClose =
    dialog.querySelector<HTMLElement>('button[aria-label="close"]') ??
    dialog.querySelector<HTMLElement>('.MuiDialogTitle-root .MuiIconButton-root');

  if (titleClose) {
    titleClose.click();
    return true;
  }

  const footerClose = dialog.querySelector<HTMLElement>('.MuiDialogActions-root .MuiButton-root');

  if (footerClose) {
    footerClose.click();
    return true;
  }

  document.dispatchEvent(
    new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', keyCode: 27, bubbles: true, cancelable: true }),
  );

  return true;
};

export const dismissTourTargetModals = (testIds: readonly string[]) => {
  for (const testId of testIds) {
    dismissTourTargetModal(testId);
  }
};

export const countTourTargets = (testIds: readonly string[]) => {
  let total = 0;

  for (const testId of testIds) {
    total += document.querySelectorAll(`[data-testid="${testId}"]`).length;
  }

  return total;
};

type ResolveFormTourHighlightTargetOptions = {
  expandModal?: boolean;
};

export const resolveFormTourHighlightTarget = (
  target: HTMLElement,
  { expandModal = false }: ResolveFormTourHighlightTargetOptions = {},
): HTMLElement => {
  const isFieldInput = target.matches('input, textarea, select') || target.getAttribute('role') === 'combobox';

  if (isFieldInput) {
    const formControl = target.closest('.MuiFormControl-root');

    if (formControl instanceof HTMLElement) {
      return formControl;
    }
  }

  if (expandModal) {
    const dialogPaper = target.closest('.MuiDialog-paper');

    if (dialogPaper instanceof HTMLElement) {
      return dialogPaper;
    }
  }

  return target;
};

export const getFormTourHighlightElement = (
  targetTestIds: readonly string[],
  { expandModal = false }: ResolveFormTourHighlightTargetOptions = {},
) => {
  const target = findTourTarget(targetTestIds);

  if (!target) {
    return null;
  }

  return resolveFormTourHighlightTarget(target, { expandModal });
};

export const scrollTourTargetIntoView = (target: HTMLElement) => {
  const highlightTarget = resolveFormTourHighlightTarget(target);
  const previousScrollMarginBottom = highlightTarget.style.scrollMarginBottom;
  const previousScrollMarginTop = highlightTarget.style.scrollMarginTop;

  highlightTarget.style.scrollMarginBottom = TOUR_SCROLL_MARGIN_BOTTOM;
  highlightTarget.style.scrollMarginTop = TOUR_SCROLL_MARGIN_TOP;
  highlightTarget.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
    inline: 'nearest',
  });

  return () => {
    highlightTarget.style.scrollMarginBottom = previousScrollMarginBottom;
    highlightTarget.style.scrollMarginTop = previousScrollMarginTop;
  };
};

export const waitForTourTarget = (
  testIds: readonly string[],
  {
    timeoutMs = TOUR_TARGET_TIMEOUT_MS,
    intervalMs = TOUR_TARGET_POLL_MS,
  }: { timeoutMs?: number; intervalMs?: number } = {},
) =>
  new Promise<HTMLElement | null>(resolve => {
    const startedAt = Date.now();

    const check = () => {
      const target = findTourTarget(testIds);

      if (target) {
        resolve(target);
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        resolve(null);
        return;
      }

      window.setTimeout(check, intervalMs);
    };

    check();
  });

export const expandTourHighlightRectBelow = (
  rect: AnchoredTourHighlightRect | null,
  extraHeight: number,
): AnchoredTourHighlightRect | null => {
  if (!rect || extraHeight <= 0) {
    return rect;
  }

  return {
    ...rect,
    height: rect.height + extraHeight,
  };
};

const delay = (ms: number) => new Promise<void>(resolve => window.setTimeout(resolve, ms));

export const advanceAfterUiSettle = async (
  advanceTo: number,
  goToStep: (stepIndex: number) => void,
  waitForTestIds?: readonly string[],
) => {
  if (waitForTestIds?.length) {
    const target = await waitForTourTarget(waitForTestIds);

    if (target) {
      goToStep(advanceTo);
    }

    return;
  }

  await delay(TOUR_UI_SETTLE_MS);
  goToStep(advanceTo);
};

const VIEWPORT_MARGIN = 16;
const PANEL_GAP = 12;
/** Fallback until the panel is measured in the DOM. */
const PANEL_ESTIMATED_HEIGHT = 280;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

type AnchoredPanelPosition = {
  top: number;
  left: number;
};

const computeAnchoredPanelPosition = (
  highlightRect: AnchoredTourHighlightRect | null,
  panelHeight: number,
  panelWidth: number,
  panelPlacement: AnchoredTourStep['panelPlacement'] = 'auto',
): AnchoredPanelPosition => {
  if (typeof window === 'undefined') {
    return { top: VIEWPORT_MARGIN, left: VIEWPORT_MARGIN };
  }

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  if (!highlightRect) {
    return {
      top: Math.max(VIEWPORT_MARGIN, (viewportHeight - panelHeight) / 2),
      left: Math.max(VIEWPORT_MARGIN, (viewportWidth - panelWidth) / 2),
    };
  }
  const maxTop = Math.max(VIEWPORT_MARGIN, viewportHeight - panelHeight - VIEWPORT_MARGIN);
  const maxLeft = Math.max(VIEWPORT_MARGIN, viewportWidth - panelWidth - VIEWPORT_MARGIN);
  const clampedLeft = clamp(highlightRect.left, VIEWPORT_MARGIN, maxLeft);
  const belowTop = highlightRect.top + highlightRect.height + PANEL_GAP;
  const aboveTop = highlightRect.top - panelHeight - PANEL_GAP;
  const rightLeft = highlightRect.left + highlightRect.width + PANEL_GAP;
  const leftLeft = highlightRect.left - panelWidth - PANEL_GAP;
  const sideTop = clamp(highlightRect.top + highlightRect.height / 2 - panelHeight / 2, VIEWPORT_MARGIN, maxTop);

  const abovePosition = (): AnchoredPanelPosition | null => {
    if (aboveTop < VIEWPORT_MARGIN) {
      return null;
    }

    return {
      top: aboveTop,
      left: clampedLeft,
    };
  };

  const belowPosition = (): AnchoredPanelPosition | null => {
    if (belowTop > maxTop) {
      return null;
    }

    return {
      top: Math.max(VIEWPORT_MARGIN, belowTop),
      left: clampedLeft,
    };
  };

  if (panelPlacement === 'above') {
    return abovePosition() ?? belowPosition() ?? { top: VIEWPORT_MARGIN, left: clampedLeft };
  }

  if (panelPlacement === 'below') {
    return belowPosition() ?? abovePosition() ?? { top: maxTop, left: clampedLeft };
  }

  const leftPosition = (): AnchoredPanelPosition | null => {
    if (leftLeft < VIEWPORT_MARGIN) {
      return null;
    }

    return {
      top: sideTop,
      left: leftLeft,
    };
  };

  const rightPosition = (): AnchoredPanelPosition | null => {
    if (rightLeft + panelWidth > viewportWidth - VIEWPORT_MARGIN) {
      return null;
    }

    return {
      top: sideTop,
      left: rightLeft,
    };
  };

  if (panelPlacement === 'left') {
    return leftPosition() ?? rightPosition() ?? abovePosition() ?? belowPosition() ?? { top: VIEWPORT_MARGIN, left: clampedLeft };
  }

  if (belowPosition()) {
    return belowPosition()!;
  }

  if (rightPosition()) {
    return rightPosition()!;
  }

  if (abovePosition()) {
    return abovePosition()!;
  }

  return {
    top: clamp(belowTop, VIEWPORT_MARGIN, maxTop),
    left: clampedLeft,
  };
};

type BlockerRect = {
  top: number;
  left: number;
  width: number;
  height: number;
};

/** Fixed overlays that surround the interaction hole so only that region receives clicks. */
const computeInteractionBlockers = (
  hole: AnchoredTourHighlightRect | null,
  viewportWidth: number,
  viewportHeight: number,
): BlockerRect[] => {
  if (!hole || hole.width <= 0 || hole.height <= 0) {
    return [{ top: 0, left: 0, width: viewportWidth, height: viewportHeight }];
  }

  const holeRight = hole.left + hole.width;
  const holeBottom = hole.top + hole.height;
  const blockers: BlockerRect[] = [];

  if (hole.top > 0) {
    blockers.push({ top: 0, left: 0, width: viewportWidth, height: hole.top });
  }

  if (hole.left > 0) {
    blockers.push({ top: hole.top, left: 0, width: hole.left, height: hole.height });
  }

  if (holeRight < viewportWidth) {
    blockers.push({
      top: hole.top,
      left: holeRight,
      width: viewportWidth - holeRight,
      height: hole.height,
    });
  }

  if (holeBottom < viewportHeight) {
    blockers.push({
      top: holeBottom,
      left: 0,
      width: viewportWidth,
      height: viewportHeight - holeBottom,
    });
  }

  return blockers;
};

const getTourHighlightColor = (theme: Theme) =>
  theme.palette.mode === 'dark' ? '#FACC15' : '#EAB308';

const highlightFrameSx = (theme: Theme, variant: 'primary' | 'context') => {
  const color = getTourHighlightColor(theme);
  const isContext = variant === 'context';

  return {
    position: 'fixed' as const,
    boxSizing: 'border-box' as const,
    borderRadius: '4px',
    pointerEvents: 'none' as const,
    zIndex: TOUR_FRAME_Z_INDEX,
    border: `1px solid ${alpha(color, theme.palette.mode === 'dark' ? (isContext ? 0.55 : 0.95) : isContext ? 0.5 : 0.9)}`,
    bgcolor: isContext ? 'transparent' : alpha(color, theme.palette.mode === 'dark' ? 0.12 : 0.14),
    transition: TOUR_POSITION_TRANSITION,
  };
};

type Props = {
  activeStepIndex: number | null;
  steps: readonly AnchoredTourStep[];
  highlightRect: AnchoredTourHighlightRect | null;
  contextHighlightRect?: AnchoredTourHighlightRect | null;
  waitingMessage?: string;
  overlineLabel: string;
  primaryLabel: string;
  showPrimaryButton?: boolean;
  onClose: () => void;
  onBack: () => void;
  onPrimaryAction: () => void;
};

const AnchoredTourGuide = ({
  activeStepIndex,
  steps,
  highlightRect,
  contextHighlightRect = null,
  waitingMessage = 'Waiting for this part of the UI to appear.',
  overlineLabel,
  primaryLabel,
  showPrimaryButton = true,
  onClose,
  onBack,
  onPrimaryAction,
}: Props) => {
  const theme = useTheme();
  const panelRef = useRef<HTMLDivElement>(null);
  const [measuredPanelHeight, setMeasuredPanelHeight] = useState(PANEL_ESTIMATED_HEIGHT);
  const commonStyles = useMemo(() => getCommonStyles(theme), [theme]);
  const cardSurface = useMemo(() => getOnboardingCardSurfaceStyles(theme), [theme]);
  const primaryFrameSx = useMemo(() => highlightFrameSx(theme, 'primary'), [theme]);
  const contextFrameSx = useMemo(() => highlightFrameSx(theme, 'context'), [theme]);

  const step = activeStepIndex === null ? null : steps[activeStepIndex];
  const backDisabled = activeStepIndex === 0 || Boolean(step?.disableBack);
  const stepCount = steps.length;
  const panelZIndex =
    showPrimaryButton && step?.showPrimaryWhenTargetTestIds?.length
      ? TOUR_MENU_Z_INDEX + 50
      : TOUR_PANEL_Z_INDEX;
  const panelWidth = typeof window === 'undefined' ? 320 : Math.min(360, window.innerWidth - 32);
  const hasActiveHighlight = Boolean(highlightRect);
  const [viewportSize, setViewportSize] = useState(() =>
    typeof window === 'undefined' ? { width: 0, height: 0 } : { width: window.innerWidth, height: window.innerHeight },
  );

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    const syncViewport = () => {
      setViewportSize({ width: window.innerWidth, height: window.innerHeight });
    };

    syncViewport();
    window.addEventListener('resize', syncViewport);

    return () => {
      window.removeEventListener('resize', syncViewport);
    };
  }, []);

  const interactionHighlightRect = useMemo(() => {
    const clickThroughRect = contextHighlightRect
      ? unionTourHighlightRects(highlightRect, contextHighlightRect)
      : highlightRect;

    return expandTourHighlightRectBelow(clickThroughRect, step?.interactionPaddingBelow ?? 0);
  }, [contextHighlightRect, highlightRect, step?.interactionPaddingBelow]);

  const interactionBlockers = useMemo(
    () => computeInteractionBlockers(interactionHighlightRect, viewportSize.width, viewportSize.height),
    [interactionHighlightRect, viewportSize.height, viewportSize.width],
  );

  useLayoutEffect(() => {
    const panel = panelRef.current;

    if (!panel || activeStepIndex === null) {
      return;
    }

    const updateHeight = () => {
      setMeasuredPanelHeight(panel.offsetHeight || PANEL_ESTIMATED_HEIGHT);
    };

    updateHeight();

    const observer = new ResizeObserver(updateHeight);
    observer.observe(panel);

    return () => observer.disconnect();
  }, [activeStepIndex, highlightRect, step?.title, step?.description, primaryLabel]);

  if (activeStepIndex === null || !step) {
    return null;
  }

  const { top: panelTop, left: panelLeft } = computeAnchoredPanelPosition(
    highlightRect,
    measuredPanelHeight,
    panelWidth,
    step.panelPlacement,
  );

  const tourGuide = (
    <>
      {interactionBlockers.map((blocker, index) => (
        <Box
          key={`tour-blocker-${index}`}
          aria-hidden
          sx={{
            position: 'fixed',
            top: blocker.top,
            left: blocker.left,
            width: blocker.width,
            height: blocker.height,
            zIndex: TOUR_BLOCKER_Z_INDEX,
            pointerEvents: 'auto',
            transition: TOUR_POSITION_TRANSITION,
          }}
        />
      ))}

      {contextHighlightRect && (
        <Box
          sx={{
            ...contextFrameSx,
            top: contextHighlightRect.top,
            left: contextHighlightRect.left,
            width: contextHighlightRect.width,
            height: contextHighlightRect.height,
          }}
        />
      )}

      {highlightRect && (
        <Box
          sx={{
            ...primaryFrameSx,
            top: highlightRect.top,
            left: highlightRect.left,
            width: highlightRect.width,
            height: highlightRect.height,
          }}
        />
      )}

      <Paper
        ref={panelRef}
        data-tour-guide-panel
        elevation={8}
        sx={{
          pointerEvents: 'auto',
          ...cardSurface,
          position: 'fixed',
          top: panelTop,
          left: panelLeft,
          width: panelWidth,
          maxWidth: 'calc(100vw - 32px)',
          maxHeight: `calc(100vh - ${VIEWPORT_MARGIN * 2}px)`,
          zIndex: panelZIndex,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          transition: TOUR_POSITION_TRANSITION,
        }}
      >
        <Box sx={[commonStyles.gradientHeader, { pb: { xs: 2, sm: 2.5 } }]}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: 1 }}>
            <Box sx={{ minWidth: 0, pr: 1 }}>
              <Typography
                variant="overline"
                sx={{
                  display: 'block',
                  lineHeight: 1.3,
                  letterSpacing: '0.08em',
                  fontWeight: 600,
                  color: alpha(theme.palette.primary.contrastText, 0.88),
                }}
              >
                {overlineLabel}
              </Typography>
              <Typography variant="h6" sx={{ fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.35, mt: 0.25 }}>
                {step.title}
              </Typography>
            </Box>

            <IconButton
              size="small"
              onClick={onClose}
              sx={{
                color: 'inherit',
                flexShrink: 0,
                mt: '-4px',
                mr: '-8px',
                opacity: 0.9,
                '&:hover': {
                  opacity: 1,
                  bgcolor: alpha(theme.palette.common.white, theme.palette.mode === 'dark' ? 0.12 : 0.18),
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        <Box
          sx={{
            p: { xs: 2, sm: 3 },
            bgcolor: theme =>
              theme.palette.mode === 'dark'
                ? alpha(theme.palette.primary.main, 0.14)
                : theme.palette.background.default,
            flexGrow: 1,
            minHeight: 0,
            overflow: 'auto',
            boxShadow: theme =>
              theme.palette.mode === 'dark' ? `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.06)}` : undefined,
          }}
        >
          <Typography variant="body2" sx={{ ...onboardingStepTextSx, mb: 2 }}>
            {step.description}
          </Typography>

          <Typography
            variant="caption"
            sx={{ display: 'block', mb: 2, color: 'text.secondary', fontWeight: 600, letterSpacing: '0.02em' }}
          >
            Step {activeStepIndex + 1} of {stepCount}
          </Typography>

          {!hasActiveHighlight && (
            <Typography variant="body2" color="warning.main" sx={{ display: 'block', mb: 2, fontWeight: 500 }}>
              {waitingMessage}
            </Typography>
          )}

          <Box
            sx={{
              display: 'flex',
              justifyContent: showPrimaryButton ? 'space-between' : 'flex-start',
              gap: 1,
            }}
          >
            <Button
              variant="contained"
              color="inherit"
              disabled={backDisabled}
              onClick={onBack}
              sx={onboardingActionButtonSx}
            >
              Back
            </Button>

            {showPrimaryButton && (
              <Button
                type="button"
                variant="contained"
                color="primary"
                onClick={event => {
                  event.stopPropagation();
                  onPrimaryAction();
                }}
                sx={onboardingActionButtonSx}
              >
                {primaryLabel}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </>
  );

  return <Portal>{tourGuide}</Portal>;
};

export default AnchoredTourGuide;
